import React, { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Combobox de factura con búsqueda por escritura (número, fecha, total).
 * Pre-filtrar el array de facturas por cliente antes de pasarlo aquí.
 */
export function FacturaCombobox({ value, onChange, facturas = [], placeholder = "Buscar factura…", testId = "fact-combo", disabled = false, className }) {
  const [open, setOpen] = useState(false);
  const selected = facturas.find(f => f.id === value);

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("h-9 justify-between font-normal text-left w-full", !selected && "text-[#8ba397]", className)}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate">
            {selected
              ? `${selected.numero || selected.factura_origen_numero || "—"} · ${selected.fecha || ""} · $${(selected.total || 0).toFixed(2)}`
              : placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px] p-0" align="start">
        <Command shouldFilter={true}>
          <div className="flex items-center border-b border-[#d1dbd6] px-3" cmdk-input-wrapper="">
            <Search size={14} className="mr-2 shrink-0 opacity-50" />
            <CommandInput
              placeholder="N° de factura, fecha, total…"
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-[#8ba397]"
              data-testid={`${testId}-input`}
            />
          </div>
          <CommandList className="max-h-[360px] overflow-y-auto">
            <CommandEmpty className="py-4 text-center text-sm text-[#8ba397]">Sin facturas</CommandEmpty>
            <CommandGroup>
              {facturas.map(f => {
                const searchable = [f.numero, f.fecha, f.cliente_nombre, (f.total || 0).toFixed(2)].filter(Boolean).join(" ");
                return (
                  <CommandItem
                    key={f.id}
                    value={searchable}
                    onSelect={() => { onChange(f.id); setOpen(false); }}
                    data-testid={`${testId}-item-${f.id}`}
                  >
                    <Check size={14} className={cn("mr-2", value === f.id ? "opacity-100 text-[#205c45]" : "opacity-0")} />
                    <div className="flex flex-col flex-1">
                      <div className="flex justify-between items-baseline">
                        <span className="font-medium font-mono">{f.numero}</span>
                        <span className="font-semibold tabular-nums text-[#205c45]">${(f.total || 0).toFixed(2)}</span>
                      </div>
                      <span className="text-[11px] text-[#5c7368]">
                        {f.fecha} · {(f.lineas || []).length} línea(s) · {f.cliente_nombre || ""}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
